// DatabaseToolbarSearchButton.jsx

import React, { useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import './DatabaseToolbarSearchButton.css';

// Icons
import Icon from 'svg/Icon';

/**
 * DatabaseToolbarSearchButton
 * 
 * Renders an expandable search input in the database toolbar.
 *  
 *
 * @returns {ReactNode} - DatabaseToolbarSearchButton.
 */
const DatabaseToolbarSearchButton = React.memo(({ value, onChange }) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Hover state

    const [hovered, setHovered] = useState(false);
    const [expanded, setExpanded] = useState(false);

    // ----- REFS ----- //

    const inputRef = useRef(null);

    // ----- HANDLERS ----- //

    /**
     * handleExpandToggle
     *
     * Handles expand toggle.
     */
    const handleExpandToggle = () => {
        if (expanded) {
            setExpanded(false);
            onChange?.('');
            return;
        }

        setExpanded(true);
        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    };

    /**
     * handleInputChange
     *
     * Handles input change.
     *
     * @param {object} event - event.
     */
    const handleInputChange = (event) => {
        onChange?.(event.target.value);
    };

    /**
     * handleInputKeyDown
     *
     * Handles input key down.
     *
     * @param {object} event - event.
     */
    const handleInputKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        onChange?.('');
        setExpanded(false);
    };

    /**
     * handleClear
     *
     * Handles clear.
     */
    const handleClear = () => {
        onChange?.('');
        setExpanded(false);
    };

    // ----- RENDER ----- //

    return (

        <div
          className={`database-toolbar-search-button-container${expanded ? ' expanded' : ''}`}
          style={{
                backgroundColor: hovered || expanded ? theme.backgroundMuted : 'transparent',
            }}
          title="Filter items"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >

            {/* ICON */}
            <button
              className="database-toolbar-search-button-icon"
              type="button"
              title={expanded ? 'Clear filter' : 'Filter'}
              aria-label={expanded ? 'Clear filter' : 'Filter'}
              onClick={handleExpandToggle}
            >
                <Icon
                  iconKey="search"
                  color={hovered || expanded ? theme.foreground : theme.foregroundMuted}
                  width={20}
                  height={20}
                />
            </button>

            {expanded && (
                <>
                    {/* INPUT */}
                    <input
                      className="database-toolbar-search-button-input"
                      ref={inputRef}
                      type="text"
                      value={value || ''}
                      placeholder="Filter"
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                    />

                    {/* CLEAR */}
                    <button
                      className="database-toolbar-search-button-clear"
                      type="button"
                      title="Clear filter"
                      aria-label="Clear filter"
                      onClick={handleClear}
                    >
                        <Icon
                          iconKey="close"
                          color={theme.foregroundMuted}
                          width={16}
                          height={16}
                        />
                    </button>
                </>
            )}

        </div>
    );

});

// Dev Tools
DatabaseToolbarSearchButton.displayName = 'DatabaseToolbarSearchButton';

export default DatabaseToolbarSearchButton;
